import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import MySignatureButton from "@/components/signatures/MySignatureButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  LayoutDashboard, Car, Store, Wrench, Users, HardHat, ArrowLeft, LogOut, Menu, Receipt,
  Warehouse, Truck, PieChart, Settings, Layers, Search, Package,
} from "lucide-react";
import LowStockBanner from "@/mechanic/components/LowStockBanner";
import BusinessSwitcher from "@/components/BusinessSwitcher";
import api, { money, fmtDate } from "@/mechanic/lib/api";

const navItems = [
  { to: "/mechanic", label: "Dashboard", icon: LayoutDashboard, testid: "mech-nav-dashboard", end: true },
  { to: "/mechanic/shop-view", label: "Shop View", icon: Warehouse, testid: "mech-nav-shop-view" },
  { to: "/mechanic/work-orders", label: "Work Orders", icon: Wrench, testid: "mech-nav-work-orders" },
  { to: "/mechanic/customers", label: "Customers", icon: Users, testid: "mech-nav-customers" },
  { to: "/mechanic/inventory", label: "Inventory & Expenses", icon: Package, testid: "mech-nav-inventory" },
  { to: "/mechanic/suppliers", label: "Suppliers", icon: Truck, testid: "mech-nav-suppliers" },
  { to: "/mechanic/financials", label: "Financials", icon: PieChart, testid: "mech-nav-financials" },
  { to: "/mechanic/invoices", label: "Invoices", icon: Receipt, testid: "mech-nav-invoices" },
  { to: "/mechanic/rental-fleet", label: "Rental Fleet", icon: Car, testid: "mech-nav-rental" },
  { to: "/mechanic/dealership-cars", label: "Dealership Cars", icon: Store, testid: "mech-nav-dealership" },
  { to: "/mechanic/mechanics", label: "Mechanics", icon: HardHat, testid: "mech-nav-mechanics" },
  { to: "/mechanic/canned-jobs", label: "Canned Jobs", icon: Layers, testid: "mech-nav-canned" },
  { to: "/mechanic/settings", label: "Shop Settings", icon: Settings, testid: "mech-nav-settings" },
];

function GlobalSearch() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null); // {customers:[]}
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!q || q.trim().length < 2) { setResults(null); setLoading(false); return; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search`, { params: { q: q.trim() } });
        setResults(data);
        setOpen(true);
      } catch { setResults(null); }
      finally { setLoading(false); }
    }, 300);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (path) => { setOpen(false); setQ(""); setResults(null); navigate(path); };
  const cust = results?.customers || [];

  return (
    <div ref={boxRef} className="relative w-full max-w-md" data-testid="mech-global-search">
      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => { if (results) setOpen(true); }}
          placeholder="Search customers, vehicles or work orders…"
          className="pl-9"
          data-testid="mech-global-search-input"
        />
      </div>
      {open && (loading || cust.length > 0 || (q.trim().length >= 2 && !loading)) && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-md shadow-lg z-40 max-h-[70vh] overflow-y-auto" data-testid="mech-global-search-results">
          {loading && <div className="p-3 text-sm text-muted-foreground">Searching…</div>}
          {!loading && cust.length === 0 && q.trim().length >= 2 && (
            <div className="p-3 text-sm text-muted-foreground">No matches for &quot;{q}&quot;.</div>
          )}
          {!loading && cust.map((row) => (
            <div key={row.customer.id} className="border-b border-border last:border-b-0 p-3 hover:bg-accent">
              <button
                className="text-left w-full"
                onClick={() => go(`/mechanic/customers/${row.customer.id}`)}
                data-testid={`mech-search-cust-${row.customer.id}`}
              >
                <div className="font-semibold text-sm">{row.customer.name}</div>
                <div className="text-xs text-muted-foreground">
                  {row.customer.phone || "—"}{row.customer.email ? ` · ${row.customer.email}` : ""}
                </div>
              </button>
              {(row.vehicles || []).length > 0 && (
                <div className="mt-1.5 pl-2 border-l border-border space-y-0.5">
                  {row.vehicles.slice(0, 4).map((v) => (
                    <div key={v.id} className="text-xs text-muted-foreground">
                      {v.year} {v.make} {v.model} {v.plate ? `· ${v.plate}` : ""}
                    </div>
                  ))}
                </div>
              )}
              {(row.work_orders || []).length > 0 && (
                <div className="mt-1.5 pl-2 border-l border-border space-y-0.5">
                  {row.work_orders.slice(0, 3).map((w) => (
                    <button
                      key={w.id}
                      onClick={() => go(`/mechanic/work-orders/${w.id}`)}
                      className="block text-xs text-primary hover:underline"
                      data-testid={`mech-search-wo-${w.id}`}
                    >
                      WO · {fmtDate(w.service_date)} · {money(w?.totals?.total || 0)}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function MechanicLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors duration-150 ${
      isActive ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground"
    }`;

  const SidebarBody = ({ onNavigate }) => ( // eslint-disable-line react/no-unstable-nested-components
    <>
      <div className="px-5 py-6 border-b border-border">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-xl bg-primary/12 border border-primary/25 flex items-center justify-center shrink-0">
            <Wrench className="h-5 w-5 text-primary" />
          </span>
          <div className="min-w-0">
            <div className="font-heading text-lg leading-tight truncate">01 Auto Care</div>
            <div className="label-mono mt-0.5">Mechanic Shop</div>
          </div>
        </div>
      </div>
      <div className="px-3 pt-3">
        <NavLink to="/" onClick={onNavigate} data-testid="mech-back-to-rental" className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-muted-foreground hover:bg-white/[0.04] hover:text-foreground transition-colors">
          <ArrowLeft className="h-4 w-4" /> Back to Rental CRM
        </NavLink>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {navItems.map((item) => (
          <NavLink key={item.to} to={item.to} end={item.end} onClick={onNavigate} data-testid={item.testid} className={linkClass}>
            <item.icon className="h-[18px] w-[18px]" />
            {item.label}
          </NavLink>
        ))}
      </nav>
      <div className="px-3 py-4 border-t border-border">
        <div className="flex items-center gap-3 px-3 py-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
          <span className="w-9 h-9 rounded-full bg-primary/15 border border-primary/25 text-primary flex items-center justify-center font-heading text-sm shrink-0">
            {(user?.name || "?").trim().charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-sm truncate" data-testid="mech-current-user-name">{user?.name}</p>
            <p className="label-mono mt-0.5">{user?.role}</p>
          </div>
          <MySignatureButton />
          <button onClick={handleLogout} data-testid="mech-logout" title="Sign out" aria-label="Sign out"
            className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-white/[0.06] transition-colors shrink-0">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-background grain-overlay">
      <aside className="w-64 shrink-0 bg-card/40 border-r border-border hidden md:flex flex-col fixed h-screen overflow-y-auto">
        <SidebarBody />
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-card flex flex-col md:hidden" data-testid="mech-mobile-sidebar">
          <SheetTitle className="sr-only">Mechanic navigation</SheetTitle>
          <SidebarBody onNavigate={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
      <main className="flex-1 md:ml-64 min-h-screen min-w-0 relative z-10">
        {/* Desktop top bar with global search */}
        <header className="hidden md:flex sticky top-0 z-30 bg-background/70 backdrop-blur-xl border-b border-border items-center gap-4 px-6 h-16">
          <BusinessSwitcher />
          <GlobalSearch />
        </header>
        {/* Mobile top bar with menu + search */}
        <header className="md:hidden sticky top-0 z-30 bg-background/70 backdrop-blur-xl border-b border-border flex items-center gap-3 px-4 h-16">
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} data-testid="mech-mobile-menu" aria-label="Open menu"><Menu className="h-5 w-5" /></Button>
          <span className="font-heading text-base flex items-center gap-2"><Wrench className="h-5 w-5 text-primary" />01 Auto Care</span>
        </header>
        <div className="md:hidden px-4 pt-3">
          <GlobalSearch />
        </div>
        <LowStockBanner />
        <Outlet />
      </main>
    </div>
  );
}
